import React, {
  FormEvent,
  ReactNode,
  startTransition,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { IS_SUPABASE_CONFIGURED } from "../../../common/network/SupabaseClient";
import InteractiveClinicMap from "../components/InteractiveClinicMap";
import { fetchClinicServices } from "../data/fetchClinicServices";
import { saveClinicOnboardingSubmission } from "../data/saveClinicOnboardingSubmission";
import {
  buildClinicMapViewHref,
  hasCoordinates,
  MAPBOX_ACCESS_TOKEN,
  MAPBOX_COUNTRY,
  MAPBOX_LANGUAGE,
} from "../mapbox";
import type {
  ClinicOnboardingLocation,
  ClinicOnboardingPayload,
  ServiceOption,
  VerifiedAddress,
} from "../types";
import styles from "./ClinicOnboardingPage.module.css";

type FeedbackKind = "" | "success" | "error" | "warning";

type FeedbackState = {
  kind: FeedbackKind;
  message: string;
};

type AddressSuggestion = {
  id: string;
  label: string;
  meta: string;
  resolve: () => Promise<VerifiedAddress>;
};

type MapboxFeature = {
  id?: string;
  place_name?: string;
  geometry?: {
    coordinates?: number[];
  };
  properties?: Record<string, unknown>;
};

const CURRENCY_OPTIONS = ["USD", "EUR"];
const CLINIC_STEP_FIELD_KEYS = [
  "clinicName",
  "emailAddress",
  "pointOfContact",
  "pointOfContactEmail",
  "phoneNumber",
] as const;
const LOCATION_STEP_FIELD_KEYS = [
  "verifiedAddress",
  "services",
  "pricing",
  "locations",
] as const;

type OnboardingView = "clinic" | "locations" | "success";

function formatPhoneNumber(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (!digits) return "";
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

function extractPhoneDigits(value: string) {
  return value.replace(/\D/g, "").slice(0, 10);
}

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function readString(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function getMapboxContextEntry(context: unknown, keys: string[]) {
  if (!context || typeof context !== "object") {
    return null;
  }

  const record = context as Record<string, unknown>;
  for (const key of keys) {
    if (record[key]) {
      return record[key];
    }
  }

  return null;
}

function getMapboxContextValue(entry: unknown, preferredKeys: string[]) {
  if (typeof entry === "string") {
    return entry.trim();
  }

  if (!entry || typeof entry !== "object") {
    return "";
  }

  const record = entry as Record<string, unknown>;
  for (const key of preferredKeys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return "";
}

function normalizeRegionCode(value: string) {
  if (!value) {
    return "";
  }

  const segments = value.trim().split("-");
  return segments[segments.length - 1] || "";
}

function createMapboxVerifiedAddress(feature: MapboxFeature) {
  const properties =
    feature.properties && typeof feature.properties === "object"
      ? feature.properties
      : {};
  const context = properties.context;
  const coordinates: number[] = Array.isArray(feature.geometry?.coordinates)
    ? (feature.geometry?.coordinates as number[])
    : [];
  const [longitudeValue, latitudeValue] = coordinates;
  const longitude =
    typeof longitudeValue === "number" && Number.isFinite(longitudeValue)
      ? longitudeValue
      : null;
  const latitude =
    typeof latitudeValue === "number" && Number.isFinite(latitudeValue)
      ? latitudeValue
      : null;

  const placeContext = getMapboxContextEntry(context, [
    "place",
    "locality",
    "district",
  ]);
  const regionContext = getMapboxContextEntry(context, ["region"]);
  const postcodeContext = getMapboxContextEntry(context, ["postcode"]);
  const countryContext = getMapboxContextEntry(context, ["country"]);

  const line1 =
    readString(properties.name) ||
    readString(properties.address) ||
    readString(feature.place_name).split(",")[0]?.trim() ||
    "";
  const placeFormatted = readString(properties.place_formatted);
  const formattedAddress =
    readString(properties.full_address) ||
    [line1, placeFormatted].filter(Boolean).join(", ") ||
    readString(feature.place_name);
  const city =
    getMapboxContextValue(placeContext, ["name", "text"]) ||
    readString(properties.place);
  const state = normalizeRegionCode(
    getMapboxContextValue(regionContext, [
      "region_code",
      "region_code_full",
      "short_code",
      "name",
      "text",
    ]) || readString(properties.region)
  );
  const postalCode =
    getMapboxContextValue(postcodeContext, ["name", "text"]) ||
    readString(properties.postcode);
  const country = (
    getMapboxContextValue(countryContext, [
      "country_code",
      "short_code",
      "code",
      "name",
      "text",
    ]) || readString(properties.country)
  ).toUpperCase();
  const placeId = readString(properties.mapbox_id) || readString(feature.id);

  if (!formattedAddress) {
    return null;
  }

  return {
    formattedAddress,
    line1,
    city,
    state,
    postalCode,
    country,
    placeId,
    latitude,
    longitude,
    verificationSource: "mapbox-geocoding" as const,
  };
}

async function getMapboxAddressSuggestions(
  query: string
): Promise<AddressSuggestion[]> {
  if (!MAPBOX_ACCESS_TOKEN) {
    return [];
  }

  const params = new URLSearchParams({
    q: query,
    access_token: MAPBOX_ACCESS_TOKEN,
    autocomplete: "true",
    limit: "6",
    types: "address,street",
    permanent: "true",
  });

  if (MAPBOX_LANGUAGE) {
    params.set("language", MAPBOX_LANGUAGE);
  }

  if (MAPBOX_COUNTRY) {
    params.set("country", MAPBOX_COUNTRY.toLowerCase());
  }

  const response = await fetch(
    `https://api.mapbox.com/search/geocode/v6/forward?${params.toString()}`
  );

  if (!response.ok) {
    throw new Error(`Mapbox lookup failed with status ${response.status}.`);
  }

  const data = await response.json();
  const features = Array.isArray(data?.features) ? data.features : [];

  return features
    .map((feature: MapboxFeature, index: number) => {
      const resolvedAddress = createMapboxVerifiedAddress(feature);
      if (!resolvedAddress) {
        return null;
      }

      const localityLabel =
        resolvedAddress.city ||
        resolvedAddress.state ||
        resolvedAddress.country ||
        "Verified by Mapbox";

      return {
        id:
          resolvedAddress.placeId ||
          `${resolvedAddress.formattedAddress}-${index}`,
        label: resolvedAddress.formattedAddress,
        meta: `Mapbox verified suggestion • ${localityLabel}`,
        resolve: async () => resolvedAddress,
      };
    })
    .filter(Boolean) as AddressSuggestion[];
}

function feedbackClassName(kind: FeedbackKind) {
  if (kind === "success") return styles.metaSuccess;
  if (kind === "error") return styles.metaError;
  if (kind === "warning") return styles.metaWarning;
  return "";
}

function toastClassName(kind: FeedbackKind) {
  if (kind === "success") return styles.toastSuccess;
  if (kind === "error") return styles.toastError;
  if (kind === "warning") return styles.toastWarning;
  return "";
}

function toastTitle(kind: FeedbackKind) {
  if (kind === "success") return "Success";
  if (kind === "error") return "Unable to save";
  if (kind === "warning") return "Needs attention";
  return "Status";
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return "An unexpected error occurred.";
}

function getServiceSummary(service: ServiceOption) {
  return (
    service.description?.trim() ||
    "Service description is not available yet."
  );
}

function getServiceMonogram(label: string) {
  return label.trim().charAt(0).toUpperCase() || "S";
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function renderHighlightedText(text: string, query: string): ReactNode {
  const trimmedText = text.trim();
  const trimmedQuery = query.trim();

  if (!trimmedText || !trimmedQuery) {
    return trimmedText || text;
  }

  const matcher = new RegExp(`(${escapeRegExp(trimmedQuery)})`, "ig");
  const parts = trimmedText.split(matcher);

  if (parts.length === 1) {
    return trimmedText;
  }

  return parts.map((part, index) =>
    part.toLowerCase() === trimmedQuery.toLowerCase() ? (
      <mark key={`${part}-${index}`} className={styles.searchHighlight}>
        {part}
      </mark>
    ) : (
      <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    )
  );
}

function createLocationId() {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `location-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const ClinicOnboardingPage = () => {
  const isSupabaseReady = IS_SUPABASE_CONFIGURED;
  const [activeView, setActiveView] = useState<OnboardingView>("clinic");
  const [clinicName, setClinicName] = useState("");
  const [pointOfContact, setPointOfContact] = useState("");
  const [pointOfContactEmail, setPointOfContactEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [savedLocations, setSavedLocations] = useState<ClinicOnboardingLocation[]>(
    []
  );
  const [locationFeedback, setLocationFeedback] = useState<FeedbackState>({
    kind: "",
    message: "",
  });
  const [serviceQuery, setServiceQuery] = useState("");
  const [serviceCatalog, setServiceCatalog] = useState<ServiceOption[]>([]);
  const [isLoadingServices, setIsLoadingServices] = useState(true);
  const [servicesFeedback, setServicesFeedback] = useState<FeedbackState>({
    kind: "",
    message: "",
  });
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [pricingValues, setPricingValues] = useState<Record<string, string>>({});
  const [serviceCurrencies, setServiceCurrencies] = useState<Record<string, string>>(
    {}
  );
  const [priceErrors, setPriceErrors] = useState<Record<string, string>>({});
  const [addressQuery, setAddressQuery] = useState("");
  const [selectedAddress, setSelectedAddress] = useState<VerifiedAddress | null>(
    null
  );
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>(
    []
  );
  const [isResolvingAddress, setIsResolvingAddress] = useState(false);
  const [addressFeedback, setAddressFeedback] = useState<FeedbackState>({
    kind: "",
    message: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submissionFeedback, setSubmissionFeedback] = useState<FeedbackState>({
    kind: "",
    message: "",
  });
  const [submittedClinicName, setSubmittedClinicName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const addressRequestIdRef = useRef(0);

  const deferredServiceQuery = useDeferredValue(serviceQuery);
  const deferredAddressQuery = useDeferredValue(addressQuery);
  const selectedAddressExternalMapUrl = buildClinicMapViewHref(selectedAddress);
  const serviceLookup = useMemo(
    () => new Map(serviceCatalog.map((service) => [service.id, service])),
    [serviceCatalog]
  );

  const selectedServices = useMemo(
    () =>
      selectedServiceIds
        .map((serviceId) => serviceLookup.get(serviceId))
        .filter(Boolean) as ServiceOption[],
    [selectedServiceIds, serviceLookup]
  );

  const serviceSuggestions = useMemo(() => {
    const normalizedQuery = deferredServiceQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return [];
    }

    return serviceCatalog.filter((service) => {
      if (selectedServiceIds.includes(service.id)) return false;
      const normalizedLabel = service.label.toLowerCase();
      const normalizedDescription = (service.description || "").toLowerCase();

      return (
        normalizedLabel.includes(normalizedQuery) ||
        normalizedDescription.includes(normalizedQuery)
      );
    }).slice(0, 8);
  }, [deferredServiceQuery, selectedServiceIds, serviceCatalog]);

  useEffect(() => {
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  }, [activeView]);

  useEffect(() => {
    if (!submissionFeedback.message && !locationFeedback.message) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setSubmissionFeedback({ kind: "", message: "" });
      setLocationFeedback({ kind: "", message: "" });
    }, 5000);

    return () => window.clearTimeout(timeoutId);
  }, [
    locationFeedback.kind,
    locationFeedback.message,
    submissionFeedback.kind,
    submissionFeedback.message,
  ]);

  useEffect(() => {
    let isCancelled = false;
    setIsLoadingServices(true);
    setServicesFeedback({ kind: "", message: "" });

    fetchClinicServices()
      .then((services) => {
        if (isCancelled) {
          return;
        }

        setServiceCatalog(services);
        if (!services.length) {
          setServicesFeedback({
            kind: "warning",
            message: "No services are available yet.",
          });
        }
      })
      .catch(() => {
        if (isCancelled) {
          return;
        }

        setServiceCatalog([]);
        setServicesFeedback({
          kind: "error",
          message: "Services are unavailable right now.",
        });
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingServices(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    const query = deferredAddressQuery.trim();

    if (!query || (selectedAddress && query === selectedAddress.formattedAddress)) {
      setAddressSuggestions([]);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      const requestId = ++addressRequestIdRef.current;
      let liveSuggestions: AddressSuggestion[] = [];

      try {
        liveSuggestions = await getMapboxAddressSuggestions(query);
      } catch (_) {
        liveSuggestions = [];
      }

      if (addressRequestIdRef.current !== requestId) {
        return;
      }

      if (liveSuggestions.length) {
        setAddressSuggestions(liveSuggestions);
        return;
      }

      setAddressSuggestions([]);
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [deferredAddressQuery, selectedAddress]);

  const clearFormError = (fieldName: string) => {
    setFormErrors((current) => {
      if (!current[fieldName]) return current;
      const nextErrors = { ...current };
      delete nextErrors[fieldName];
      return nextErrors;
    });
  };

  const clearPriceError = (serviceId: string) => {
    setPriceErrors((current) => {
      if (!current[serviceId]) return current;
      const nextErrors = { ...current };
      delete nextErrors[serviceId];
      return nextErrors;
    });
  };

  const replaceErrorSubset = (
    keys: readonly string[],
    nextSubset: Record<string, string>
  ) => {
    setFormErrors((current) => {
      const nextErrors = { ...current };
      keys.forEach((key) => {
        delete nextErrors[key];
      });

      return {
        ...nextErrors,
        ...nextSubset,
      };
    });
  };

  const clearLocationDraft = (
    options: { preserveLocationFeedback?: boolean } = {}
  ) => {
    setServiceQuery("");
    setSelectedServiceIds([]);
    setPricingValues({});
    setServiceCurrencies({});
    setPriceErrors({});
    setAddressQuery("");
    setSelectedAddress(null);
    setAddressSuggestions([]);
    setIsResolvingAddress(false);
    setAddressFeedback({ kind: "", message: "" });
    replaceErrorSubset(LOCATION_STEP_FIELD_KEYS, {});

    if (!options.preserveLocationFeedback) {
      setLocationFeedback({ kind: "", message: "" });
    }
  };

  const addService = (serviceId: string) => {
    if (selectedServiceIds.includes(serviceId)) return;
    setSelectedServiceIds((current) => [...current, serviceId]);
    setPricingValues((current) => ({ ...current, [serviceId]: current[serviceId] || "" }));
    setServiceCurrencies((current) => ({
      ...current,
      [serviceId]: current[serviceId] || "USD",
    }));
    setServiceQuery("");
    clearFormError("services");
  };

  const removeService = (serviceId: string) => {
    setSelectedServiceIds((current) => current.filter((id) => id !== serviceId));
    setPricingValues((current) => {
      const nextValues = { ...current };
      delete nextValues[serviceId];
      return nextValues;
    });
    setServiceCurrencies((current) => {
      const nextCurrencies = { ...current };
      delete nextCurrencies[serviceId];
      return nextCurrencies;
    });
    setPriceErrors((current) => {
      const nextErrors = { ...current };
      delete nextErrors[serviceId];
      return nextErrors;
    });
  };

  const handleAddressInputChange = (value: string) => {
    if (selectedAddress && value !== selectedAddress.formattedAddress) {
      setSelectedAddress(null);
    }
    setAddressQuery(value);
    setAddressFeedback({ kind: "", message: "" });
    clearFormError("verifiedAddress");
  };

  const handleSelectAddress = async (suggestion: AddressSuggestion) => {
    setIsResolvingAddress(true);
    setAddressSuggestions([]);

    try {
      const resolvedAddress = await suggestion.resolve();
      setSelectedAddress(resolvedAddress);
      setAddressQuery(resolvedAddress.formattedAddress);
      setAddressFeedback({
        kind: "success",
        message: "Address verified.",
      });
      clearFormError("verifiedAddress");
    } catch (_) {
      setSelectedAddress(null);
      setAddressFeedback({
        kind: "error",
        message:
          "That address could not be resolved. Please choose another suggestion.",
      });
    } finally {
      setIsResolvingAddress(false);
    }
  };

  const handlePriceChange = (serviceId: string, value: string) => {
    setPricingValues((current) => ({ ...current, [serviceId]: value }));
    clearPriceError(serviceId);
    clearFormError("pricing");
  };

  const handleCurrencyChange = (serviceId: string, value: string) => {
    setServiceCurrencies((current) => ({ ...current, [serviceId]: value }));
    clearPriceError(serviceId);
    clearFormError("pricing");
  };

  const handlePriceBlur = (serviceId: string) => {
    const currentValue = pricingValues[serviceId];
    if (!currentValue?.trim()) return;

    const parsedValue = Number.parseFloat(currentValue);
    if (!Number.isFinite(parsedValue) || parsedValue < 0) return;

    setPricingValues((current) => ({
      ...current,
      [serviceId]: parsedValue.toFixed(2),
    }));
  };

  const resetForm = () => {
    startTransition(() => setActiveView("clinic"));
    setClinicName("");
    setPointOfContact("");
    setPointOfContactEmail("");
    setPhoneNumber("");
    setEmailAddress("");
    setSavedLocations([]);
    setLocationFeedback({ kind: "", message: "" });
    setServiceQuery("");
    setServicesFeedback((current) =>
      current.kind === "error" ? current : { kind: "", message: "" }
    );
    clearLocationDraft({ preserveLocationFeedback: true });
    setFormErrors({});
    setSubmissionFeedback({ kind: "", message: "" });
    setSubmittedClinicName("");
  };

  const validateClinicStep = () => {
    const nextErrors: Record<string, string> = {};

    if (!clinicName.trim()) {
      nextErrors.clinicName = "Clinic name is required.";
    }

    if (!pointOfContact.trim()) {
      nextErrors.pointOfContact = "Point of contact is required.";
    }

    if (!pointOfContactEmail.trim() || !isValidEmail(pointOfContactEmail.trim())) {
      nextErrors.pointOfContactEmail =
        "Please enter a valid point of contact email address.";
    }

    if (extractPhoneDigits(phoneNumber).length !== 10) {
      nextErrors.phoneNumber = "Please enter a 10-digit phone number.";
    }

    if (!emailAddress.trim() || !isValidEmail(emailAddress.trim())) {
      nextErrors.emailAddress = "Please enter a valid clinic email address.";
    }

    replaceErrorSubset(CLINIC_STEP_FIELD_KEYS, nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const validateLocationDraft = () => {
    const nextErrors: Record<string, string> = {};
    const nextPriceErrors: Record<string, string> = {};

    if (!selectedAddress) {
      nextErrors.verifiedAddress =
        "Select a verified address suggestion before saving this location.";
    }

    if (!selectedServiceIds.length) {
      nextErrors.services = "Select at least one service to continue.";
    }

    selectedServices.forEach((service) => {
      const rawValue = pricingValues[service.id]?.trim() || "";
      const parsedValue = Number.parseFloat(rawValue);
      const currencyCode = serviceCurrencies[service.id]?.trim() || "";

      if (!currencyCode) {
        nextPriceErrors[service.id] = `Choose a currency for ${service.label}.`;
        return;
      }

      if (!rawValue) {
        nextPriceErrors[service.id] = `Enter a price for ${service.label}.`;
        return;
      }

      if (!Number.isFinite(parsedValue) || parsedValue < 0) {
        nextPriceErrors[service.id] = `Price for ${service.label} must be 0 or greater.`;
      }
    });

    if (Object.keys(nextPriceErrors).length) {
      nextErrors.pricing =
        "Each selected service needs a valid price before this location can be saved.";
    }

    replaceErrorSubset(LOCATION_STEP_FIELD_KEYS, nextErrors);
    setPriceErrors(nextPriceErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const isLocationDraftStarted = () =>
    Boolean(
      selectedAddress ||
        addressQuery.trim() ||
        selectedServiceIds.length ||
        Object.keys(pricingValues).length
    );

  const buildDraftLocation = () => {
    const normalizedPrices: Record<string, string> = {};
    const normalizedCurrencies: Record<string, string> = {};

    const services = selectedServices.map((service) => {
      normalizedPrices[service.id] = Number.parseFloat(
        pricingValues[service.id]
      ).toFixed(2);
      normalizedCurrencies[service.id] = serviceCurrencies[service.id] || "USD";

      return {
        id: service.id,
        name: service.label,
        category: service.category,
        price: Number.parseFloat(normalizedPrices[service.id]),
        currency: normalizedCurrencies[service.id],
      };
    });

    return {
      location: {
        id: createLocationId(),
        verifiedAddress: selectedAddress as VerifiedAddress,
        services,
      } satisfies ClinicOnboardingLocation,
      normalizedPrices,
      normalizedCurrencies,
    };
  };

  const handleMoveToLocations = () => {
    setSubmissionFeedback({ kind: "", message: "" });

    if (!validateClinicStep()) {
      return;
    }

    setLocationFeedback({ kind: "", message: "" });
    startTransition(() => setActiveView("locations"));
  };

  const handleClearCurrentLocation = () => {
    clearLocationDraft();
    setSubmissionFeedback({ kind: "", message: "" });
  };

  const handleSaveLocation = () => {
    if (!validateLocationDraft()) {
      return;
    }

    const { location, normalizedPrices, normalizedCurrencies } =
      buildDraftLocation();
    const nextLocationNumber = savedLocations.length + 1;

    setPricingValues((current) => ({ ...current, ...normalizedPrices }));
    setServiceCurrencies((current) => ({ ...current, ...normalizedCurrencies }));
    setSavedLocations((current) => [...current, location]);
    clearLocationDraft({ preserveLocationFeedback: true });
    setLocationFeedback({
      kind: "success",
      message: `Location ${nextLocationNumber} saved. Start another location or submit when you're ready.`,
    });
    setSubmissionFeedback({ kind: "", message: "" });
  };

  const handleRemoveSavedLocation = (locationId: string) => {
    setSavedLocations((current) =>
      current.filter((location) => location.id !== locationId)
    );
    replaceErrorSubset(LOCATION_STEP_FIELD_KEYS, {});
    setLocationFeedback({
      kind: "warning",
      message: "Saved location removed.",
    });
    setSubmissionFeedback({ kind: "", message: "" });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (activeView !== "locations") {
      return;
    }

    setSubmissionFeedback({ kind: "", message: "" });
    const hasDraftLocation = isLocationDraftStarted();

    if (!savedLocations.length) {
      replaceErrorSubset(LOCATION_STEP_FIELD_KEYS, {
        locations:
          "Save at least one clinic location before submitting.",
      });
      setLocationFeedback({
        kind: "warning",
        message: "Use Save location before submitting.",
      });
      return;
    }

    if (hasDraftLocation) {
      replaceErrorSubset(LOCATION_STEP_FIELD_KEYS, {
        locations:
          "Save or clear the current location before submitting.",
      });
      setLocationFeedback({
        kind: "warning",
        message:
          "The current location is still a draft. Save it or clear it before submitting.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const locationsForSubmission = savedLocations;
      replaceErrorSubset(LOCATION_STEP_FIELD_KEYS, {});

      const payload: ClinicOnboardingPayload = {
        schemaVersion: 3,
        source: "clinic-onboarding-standalone",
        submittedAt: new Date().toISOString(),
        clinic: {
          name: clinicName.trim(),
          email: emailAddress.trim(),
        },
        pointOfContact: {
          name: pointOfContact.trim(),
          email: pointOfContactEmail.trim(),
          phoneNumber: formatPhoneNumber(phoneNumber),
          phoneDigits: extractPhoneDigits(phoneNumber),
        },
        locations: locationsForSubmission,
        metadata: {
          mapboxConfigured: Boolean(MAPBOX_ACCESS_TOKEN),
          supabaseConfigured: isSupabaseReady,
          addressLookupMode: locationsForSubmission.length
            ? "mapbox-geocoding"
            : "unverified",
          locationCount: locationsForSubmission.length,
          servicesSource: "pnoe-service-builder-api",
        },
      };

      if (isSupabaseReady) {
        try {
          await saveClinicOnboardingSubmission(payload);
          setSubmittedClinicName(payload.clinic.name);
          setSubmissionFeedback({ kind: "", message: "" });
          setLocationFeedback({ kind: "", message: "" });
          startTransition(() => setActiveView("success"));
        } catch (error) {
          setSubmissionFeedback({
            kind: "error",
            message: `We couldn't save the submission right now. ${getErrorMessage(
              error
            )}`,
          });
        }
      } else {
        setSubmissionFeedback({
          kind: "warning",
          message:
            "Your form is ready, but online saving is unavailable right now.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toastFeedback = submissionFeedback.message
    ? submissionFeedback
    : locationFeedback.message
      ? locationFeedback
      : null;

  return (
    <div className={styles.page}>
      <div className={styles.bgOrnamentTop} aria-hidden="true" />
      <div className={styles.bgOrnamentBottom} aria-hidden="true" />

      {toastFeedback ? (
        <div
          className={`${styles.toast} ${toastClassName(toastFeedback.kind)}`}
          role={toastFeedback.kind === "error" ? "alert" : "status"}
          aria-live={toastFeedback.kind === "error" ? "assertive" : "polite"}
        >
          <p className={styles.toastTitle}>{toastTitle(toastFeedback.kind)}</p>
          <p className={styles.toastMessage}>{toastFeedback.message}</p>
        </div>
      ) : null}

      <section className={styles.shell}>
        <header className={styles.formHeader}>
          <p className={styles.eyebrow}>Clinic Intake</p>
          <h1 className={styles.title}>Clinic onboarding questionnaire</h1>
          <p className={styles.description}>
            Start with the clinic and point of contact details, then add one or
            more verified locations with services and pricing before submitting
            the onboarding form. All required fields are marked with
            <span className={styles.requiredStar}> *</span>.
          </p>
        </header>

        <form className={styles.formGrid} onSubmit={handleSubmit}>
          {activeView === "success" ? (
            <section className={styles.successScreen}>
              <p className={styles.successEyebrow}>Submission complete</p>
              <h2 className={styles.successTitle}>
                {submittedClinicName || "Clinic"} is ready for review.
              </h2>
              <p className={styles.successDescription}>
                The clinic onboarding details were saved successfully. You can
                start a new clinic intake whenever you're ready.
              </p>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary} ${styles.successAction}`}
                onClick={resetForm}
              >
                Add another clinic
              </button>
            </section>
          ) : activeView === "clinic" ? (
            <>
              <section className={styles.sectionGroup}>
                <div className={styles.sectionGroupHeader}>
                  <h2 className={styles.sectionGroupTitle}>Company</h2>
                  <p className={styles.sectionGroupNote}>
                    Enter the clinic name and primary clinic email before adding
                    locations.
                  </p>
                </div>

                <div className={styles.sectionGroupFields}>
                  <article
                    className={`${styles.fieldCard} ${
                      formErrors.clinicName ? styles.fieldCardInvalid : ""
                    }`}
                  >
                    <label className={styles.label} htmlFor="clinicName">
                      Clinic name <span className={styles.requiredStar}>*</span>
                    </label>
                    <input
                      id="clinicName"
                      className={styles.input}
                      type="text"
                      value={clinicName}
                      onChange={(event) => {
                        setClinicName(event.target.value);
                        clearFormError("clinicName");
                      }}
                      placeholder="Enter clinic name"
                      autoComplete="organization"
                    />
                    {formErrors.clinicName ? (
                      <p className={`${styles.fieldMeta} ${styles.metaError}`}>
                        {formErrors.clinicName}
                      </p>
                    ) : null}
                  </article>

                  <article
                    className={`${styles.fieldCard} ${
                      formErrors.emailAddress ? styles.fieldCardInvalid : ""
                    }`}
                  >
                    <label className={styles.label} htmlFor="emailAddress">
                      Verified clinic email{" "}
                      <span className={styles.requiredStar}>*</span>
                    </label>
                    <p className={styles.helper}>
                      Shared clinic inbox used for onboarding and listing-related
                      communication.
                    </p>
                    <input
                      id="emailAddress"
                      className={styles.input}
                      type="email"
                      value={emailAddress}
                      onChange={(event) => {
                        setEmailAddress(event.target.value);
                        clearFormError("emailAddress");
                      }}
                      placeholder="info@clinic.com"
                      autoComplete="email"
                    />
                    {formErrors.emailAddress ? (
                      <p className={`${styles.fieldMeta} ${styles.metaError}`}>
                        {formErrors.emailAddress}
                      </p>
                    ) : null}
                  </article>
                </div>
              </section>

              <section className={styles.sectionGroup}>
                <div className={styles.sectionGroupHeader}>
                  <h2 className={styles.sectionGroupTitle}>Point of Contact</h2>
                  <p className={styles.sectionGroupNote}>
                    Primary administrator details for follow-up and booking support.
                  </p>
                </div>

                <div className={styles.sectionGroupFields}>
                  <article
                    className={`${styles.fieldCard} ${
                      formErrors.pointOfContact ? styles.fieldCardInvalid : ""
                    }`}
                  >
                    <label className={styles.label} htmlFor="pointOfContact">
                      Name <span className={styles.requiredStar}>*</span>
                    </label>
                    <p className={styles.helper}>
                      Full name of the primary clinic administrator.
                    </p>
                    <input
                      id="pointOfContact"
                      className={styles.input}
                      type="text"
                      value={pointOfContact}
                      onChange={(event) => {
                        setPointOfContact(event.target.value);
                        clearFormError("pointOfContact");
                      }}
                      placeholder="Enter full name"
                      autoComplete="name"
                    />
                    {formErrors.pointOfContact ? (
                      <p className={`${styles.fieldMeta} ${styles.metaError}`}>
                        {formErrors.pointOfContact}
                      </p>
                    ) : null}
                  </article>

                  <article
                    className={`${styles.fieldCard} ${
                      formErrors.pointOfContactEmail
                        ? styles.fieldCardInvalid
                        : ""
                    }`}
                  >
                    <label
                      className={styles.label}
                      htmlFor="pointOfContactEmail"
                    >
                      Email address <span className={styles.requiredStar}>*</span>
                    </label>
                    <input
                      id="pointOfContactEmail"
                      className={styles.input}
                      type="email"
                      value={pointOfContactEmail}
                      onChange={(event) => {
                        setPointOfContactEmail(event.target.value);
                        clearFormError("pointOfContactEmail");
                      }}
                      placeholder="contact@clinic.com"
                      autoComplete="email"
                    />
                    {formErrors.pointOfContactEmail ? (
                      <p className={`${styles.fieldMeta} ${styles.metaError}`}>
                        {formErrors.pointOfContactEmail}
                      </p>
                    ) : null}
                  </article>

                  <article
                    className={`${styles.fieldCard} ${
                      formErrors.phoneNumber ? styles.fieldCardInvalid : ""
                    }`}
                  >
                    <label className={styles.label} htmlFor="phoneNumber">
                      Phone number <span className={styles.requiredStar}>*</span>
                    </label>
                    <p className={styles.helper}>
                      Required format: (XXX) XXX-XXXX.
                    </p>
                    <input
                      id="phoneNumber"
                      className={styles.input}
                      type="tel"
                      value={phoneNumber}
                      onChange={(event) => {
                        setPhoneNumber(formatPhoneNumber(event.target.value));
                        clearFormError("phoneNumber");
                      }}
                      placeholder="(555) 123-4567"
                      autoComplete="tel"
                    />
                    {formErrors.phoneNumber ? (
                      <p className={`${styles.fieldMeta} ${styles.metaError}`}>
                        {formErrors.phoneNumber}
                      </p>
                    ) : null}
                  </article>
                </div>
              </section>
            </>
          ) : (
            <>
              <div
                className={`${styles.sectionGroupHeader} ${styles.locationPageHeader}`}
              >
                <h2 className={styles.sectionGroupTitle}>Locations</h2>
                <p className={styles.sectionGroupNote}>
                  Add each clinic location with its verified address, service list,
                  and location-specific pricing.
                </p>
              </div>

              <section className={styles.sectionGroup}>
                <div className={styles.locationDraftHeader}>
                  <div>
                    <p className={styles.locationDraftEyebrow}>
                      Current location
                    </p>
                    <h3 className={styles.locationDraftTitle}>
                      Location {savedLocations.length + 1}
                    </h3>
                    <p className={styles.locationDraftNote}>
                      Verify the address, choose services, and define pricing for
                      this location before saving it.
                    </p>
                  </div>
                </div>

                <div className={styles.sectionGroupFields}>
                <article
                  className={`${styles.fieldCard} ${
                    formErrors.verifiedAddress ? styles.fieldCardInvalid : ""
                  }`}
                >
                  <label className={styles.label} htmlFor="verifiedAddressSearch">
                    Verified address <span className={styles.requiredStar}>*</span>
                  </label>
                  <p className={styles.helper}>
                    Search and select the clinic's verified address.
                  </p>
                  <input
                    id="verifiedAddressSearch"
                    className={styles.input}
                    type="search"
                    value={addressQuery}
                    onChange={(event) =>
                      handleAddressInputChange(event.target.value)
                    }
                    placeholder="Search clinic address"
                    autoComplete="off"
                  />

                  {addressQuery.trim() && !selectedAddress ? (
                    <div className={styles.suggestionsList}>
                      {addressSuggestions.length ? (
                        addressSuggestions.map((suggestion) => (
                          <button
                            key={suggestion.id}
                            type="button"
                            className={styles.suggestionItem}
                            onClick={() => handleSelectAddress(suggestion)}
                            disabled={isResolvingAddress}
                          >
                            <span className={styles.suggestionTitle}>
                              {suggestion.label}
                            </span>
                            <span className={styles.suggestionMeta}>
                              {suggestion.meta}
                            </span>
                          </button>
                        ))
                      ) : (
                        <div className={styles.suggestionEmpty}>
                          No verified address suggestions matched your search.
                        </div>
                      )}
                    </div>
                  ) : null}

                  {selectedAddress ? (
                    <>
                      <div className={styles.verifiedAddressCard}>
                        <div className={styles.verifiedAddressCopy}>
                          <p className={styles.verifiedBadge}>Verified</p>
                          <p className={styles.verifiedAddressValue}>
                            {selectedAddress.formattedAddress}
                          </p>
                          <p className={styles.verifiedAddressMeta}>
                            {`${selectedAddress.city || "Unknown city"}${
                              selectedAddress.state
                                ? `, ${selectedAddress.state}`
                                : ""
                            } ${selectedAddress.postalCode || ""} ${
                              selectedAddress.country || ""
                            }`.trim()}
                          </p>
                        </div>
                        <button
                          type="button"
                          className={`${styles.btn} ${styles.btnGhost} ${styles.btnInline}`}
                          onClick={() => {
                            setSelectedAddress(null);
                            setAddressQuery("");
                            setAddressFeedback({
                              kind: "warning",
                              message: "Address cleared.",
                            });
                          }}
                        >
                          Clear
                        </button>
                      </div>

                      <section className={styles.mapPreview}>
                        <div className={styles.mapPreviewHeader}>
                          <div>
                            <p className={styles.mapPreviewEyebrow}>
                              Map preview
                            </p>
                            <p className={styles.mapPreviewTitle}>
                              Selected clinic location
                            </p>
                          </div>
                          {selectedAddressExternalMapUrl ? (
                            <a
                              href={selectedAddressExternalMapUrl}
                              target="_blank"
                              rel="noreferrer"
                              className={`${styles.btn} ${styles.btnGhost} ${styles.btnInline}`}
                            >
                              Open large map
                            </a>
                          ) : null}
                        </div>

                        {hasCoordinates(selectedAddress) ? (
                          <div className={styles.mapFrameWrap}>
                            <InteractiveClinicMap
                              address={selectedAddress}
                              className={styles.mapFrame}
                              initialZoom={14}
                              showControls={true}
                            />
                          </div>
                        ) : (
                          <div className={styles.emptyState}>
                            <p>
                              Map preview becomes available when the selected
                              address returns coordinates.
                            </p>
                          </div>
                        )}

                        <p className={styles.mapPreviewNote}>
                          The preview keeps the selected clinic address in view
                          while the rest of the intake is completed.
                        </p>
                      </section>
                    </>
                  ) : null}

                  {addressFeedback.message ? (
                    <p
                      className={`${styles.fieldMeta} ${feedbackClassName(
                        addressFeedback.kind
                      )}`}
                    >
                      {addressFeedback.message}
                    </p>
                  ) : formErrors.verifiedAddress ? (
                    <p className={`${styles.fieldMeta} ${styles.metaError}`}>
                      {formErrors.verifiedAddress}
                    </p>
                  ) : null}
                </article>

                <article
                  className={`${styles.fieldCard} ${
                    formErrors.services ? styles.fieldCardInvalid : ""
                  }`}
                >
                  <label className={styles.label} htmlFor="serviceSearch">
                    Service selection <span className={styles.requiredStar}>*</span>
                  </label>
                  <p className={styles.helper}>
                    Search the treatment and test taxonomy, then add each service
                    that should appear in the locator and booking engine.
                  </p>
                  <input
                    id="serviceSearch"
                    className={styles.input}
                    type="search"
                    value={serviceQuery}
                    onChange={(event) => setServiceQuery(event.target.value)}
                    placeholder="Search services like General Consultation or VO2 Max Test"
                    autoComplete="off"
                    disabled={isLoadingServices || !serviceCatalog.length}
                  />

                  {serviceQuery.trim() ? (
                    <div className={styles.suggestionsList}>
                      {isLoadingServices ? (
                        <div className={styles.suggestionEmpty}>
                          Loading available services...
                        </div>
                      ) : serviceSuggestions.length ? (
                        serviceSuggestions.map((service) => (
                          <button
                            key={service.id}
                            type="button"
                            className={styles.suggestionItem}
                            onClick={() => addService(service.id)}
                          >
                            <span className={styles.serviceSuggestionLayout}>
                              {service.imageUrl ? (
                                <img
                                  className={styles.serviceSuggestionImage}
                                  src={service.imageUrl}
                                  alt={service.label}
                                  loading="lazy"
                                  decoding="async"
                                />
                              ) : (
                                <span
                                  className={styles.serviceSuggestionFallback}
                                  aria-hidden="true"
                                >
                                  {getServiceMonogram(service.label)}
                                </span>
                              )}
                              <span className={styles.serviceSuggestionCopy}>
                                <span className={styles.serviceSuggestionHeader}>
                                  <span className={styles.suggestionTitle}>
                                    {renderHighlightedText(
                                      service.label,
                                      deferredServiceQuery
                                    )}
                                  </span>
                                  <span className={styles.serviceCategoryBadge}>
                                    {service.category}
                                  </span>
                                </span>
                                <span
                                  className={styles.serviceSuggestionDescription}
                                >
                                  {renderHighlightedText(
                                    getServiceSummary(service),
                                    deferredServiceQuery
                                  )}
                                </span>
                              </span>
                            </span>
                          </button>
                        ))
                      ) : (
                        <div className={styles.suggestionEmpty}>
                          No matching services are available right now.
                        </div>
                      )}
                    </div>
                  ) : null}

                  <div className={styles.selectedServices}>
                    {selectedServices.length ? (
                      selectedServices.map((service) => (
                        <div key={service.id} className={styles.serviceChip}>
                          {service.imageUrl ? (
                            <img
                              className={styles.serviceChipImage}
                              src={service.imageUrl}
                              alt={service.label}
                              loading="lazy"
                              decoding="async"
                            />
                          ) : (
                            <span
                              className={styles.serviceChipFallback}
                              aria-hidden="true"
                            >
                              {getServiceMonogram(service.label)}
                            </span>
                          )}
                          <div className={styles.serviceChipCopy}>
                            <div className={styles.serviceChipHeader}>
                              <span className={styles.serviceChipTitle}>
                                {service.label}
                              </span>
                              <span className={styles.serviceCategoryBadge}>
                                {service.category}
                              </span>
                            </div>
                            <span className={styles.serviceChipDescription}>
                              {getServiceSummary(service)}
                            </span>
                          </div>
                          <button
                            type="button"
                            className={styles.serviceChipRemove}
                            onClick={() => removeService(service.id)}
                            aria-label={`Remove ${service.label}`}
                          >
                            ×
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className={styles.selectedServicesEmpty}>
                        {isLoadingServices
                          ? "Loading services..."
                          : "No services selected yet."}
                      </p>
                    )}
                  </div>

                  {servicesFeedback.message ? (
                    <p
                      className={`${styles.fieldMeta} ${feedbackClassName(
                        servicesFeedback.kind
                      )}`}
                    >
                      {servicesFeedback.message}
                    </p>
                  ) : null}

                  {formErrors.services ? (
                    <p className={`${styles.fieldMeta} ${styles.metaError}`}>
                      {formErrors.services}
                    </p>
                  ) : null}
                </article>

                <article
                  className={`${styles.fieldCard} ${
                    formErrors.pricing ? styles.fieldCardInvalid : ""
                  }`}
                >
                  <label className={styles.label}>
                    Dynamic pricing <span className={styles.requiredStar}>*</span>
                  </label>
                  <p className={styles.helper}>
                    Every selected service gets its own currency and price fields
                    so published pricing stays synchronized service by service.
                  </p>

                  {selectedServices.length ? (
                    <div className={styles.pricingList}>
                      {selectedServices.map((service) => (
                        <div
                          key={service.id}
                          className={`${styles.pricingRow} ${
                            priceErrors[service.id]
                              ? styles.pricingRowInvalid
                              : ""
                          }`}
                        >
                          <div className={styles.pricingServiceCopy}>
                            <p className={styles.pricingServiceTitle}>
                              {service.label}
                            </p>
                            <p className={styles.pricingServiceCategory}>
                              {service.category}
                            </p>
                          </div>
                          <div className={styles.pricingControls}>
                            <div className={styles.pricingField}>
                              <label
                                className={styles.pricingMiniLabel}
                                htmlFor={`service-currency-${service.id}`}
                              >
                                Currency
                              </label>
                              <select
                                id={`service-currency-${service.id}`}
                                className={`${styles.select} ${styles.pricingSelect}`}
                                value={serviceCurrencies[service.id] || "USD"}
                                onChange={(event) =>
                                  handleCurrencyChange(
                                    service.id,
                                    event.target.value
                                  )
                                }
                              >
                                {CURRENCY_OPTIONS.map((currencyCode) => (
                                  <option
                                    key={currencyCode}
                                    value={currencyCode}
                                  >
                                    {currencyCode}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className={styles.pricingField}>
                              <label
                                className={styles.pricingMiniLabel}
                                htmlFor={`service-price-${service.id}`}
                              >
                                Price
                              </label>
                              <input
                                id={`service-price-${service.id}`}
                                className={styles.pricingInput}
                                type="number"
                                min="0"
                                step="0.01"
                                inputMode="decimal"
                                value={pricingValues[service.id] || ""}
                                onChange={(event) =>
                                  handlePriceChange(
                                    service.id,
                                    event.target.value
                                  )
                                }
                                onBlur={() => handlePriceBlur(service.id)}
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                          {priceErrors[service.id] ? (
                            <p className={`${styles.fieldMeta} ${styles.metaError}`}>
                              {priceErrors[service.id]}
                            </p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.emptyState}>
                      <p>
                        Select one or more services above to generate pricing
                        inputs.
                      </p>
                    </div>
                  )}

                  {formErrors.pricing ? (
                    <p className={`${styles.fieldMeta} ${styles.metaError}`}>
                      {formErrors.pricing}
                    </p>
                  ) : null}
                </article>
              </div>

              <div className={styles.locationDraftActions}>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnSecondary}`}
                  onClick={handleClearCurrentLocation}
                  disabled={
                    isSubmitting ||
                    isResolvingAddress ||
                    !isLocationDraftStarted()
                  }
                >
                  Clear current location
                </button>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  onClick={handleSaveLocation}
                  disabled={
                    isSubmitting ||
                    isResolvingAddress ||
                    !isLocationDraftStarted()
                  }
                >
                  Save location
                </button>
              </div>
              </section>

              <section
                className={`${styles.sectionGroup} ${styles.savedLocationsSection}`}
              >
                  <div className={styles.savedLocationsHeader}>
                    <div>
                      <p className={styles.savedLocationsEyebrow}>
                        Saved locations
                      </p>
                      <p className={styles.savedLocationsTitle}>
                        {savedLocations.length
                          ? `${savedLocations.length} location${
                              savedLocations.length === 1 ? "" : "s"
                            } ready for submission.`
                          : "No saved locations yet."}
                      </p>
                    </div>
                  </div>

                  {savedLocations.length ? (
                    <div className={styles.savedLocationsList}>
                      {savedLocations.map((location, index) => {
                        const previewServices = location.services
                          .slice(0, 3)
                          .map((service) => service.name)
                          .join(", ");

                        return (
                          <article
                            key={location.id}
                            className={styles.savedLocationCard}
                          >
                            <div className={styles.savedLocationCopy}>
                              <p className={styles.savedLocationLabel}>
                                Location {index + 1}
                              </p>
                              <p className={styles.savedLocationAddress}>
                                {location.verifiedAddress.formattedAddress}
                              </p>
                              <p className={styles.savedLocationMeta}>
                                {location.services.length} service
                                {location.services.length === 1 ? "" : "s"}
                                {previewServices ? ` • ${previewServices}` : ""}
                                {location.services.length > 3
                                  ? ` +${location.services.length - 3} more`
                                  : ""}
                              </p>
                            </div>
                            <button
                              type="button"
                              className={styles.savedLocationRemove}
                              onClick={() =>
                                handleRemoveSavedLocation(location.id)
                              }
                            >
                              Remove
                            </button>
                          </article>
                        );
                      })}
                    </div>
                  ) : (
                    <div className={styles.emptyState}>
                      <p>
                        Save the current location to add it here and enable
                        submission.
                      </p>
                    </div>
                  )}
                </section>
            </>
          )}

          {activeView !== "success" ? (
            <div className={styles.actions}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={resetForm}
              >
                Reset
              </button>
              {activeView === "clinic" ? (
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  onClick={handleMoveToLocations}
                >
                  Add location
                </button>
              ) : (
                <button
                  type="submit"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  disabled={
                    isSubmitting ||
                    isResolvingAddress ||
                    !savedLocations.length
                  }
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </button>
              )}
            </div>
          ) : null}
        </form>
      </section>
    </div>
  );
};

export default ClinicOnboardingPage;
